import { useCallback, useEffect, useRef, useState } from "react";
import ApiRoutes from "../../routes/routeConstants/apiRoutes";
import ReportService from "../../services/Report/report.service";
import Notification from "../components/Notification";
import { NotificationTypes } from "../../enums/notificationTypes";

/**
 * Self-contained dictation hook for the floating NotesPanel. Supports both
 * Deepgram (default) and Augnito (when the user has `augnito_stt` feature).
 * Mirrors the logic in ReportForm's `startSpeechRecognition` /
 * `startAugnitoSpeechRecognition` but does not touch any ReportForm state.
 *
 * ReportForm is left unchanged.
 */

declare const MediaRecorder: any;

export interface UseNotesDictationOptions {
  userRole: string | undefined;
  userId: number | undefined;
  caseDetailId: number | undefined;
  useAugnito: boolean;
  /** Called with appended text chunks (final transcripts only). */
  onTranscript: (text: string) => void;
  /** Called with partial text (Augnito only). Consumer renders preview. */
  onPartial?: (text: string) => void;
}

interface DictationState {
  isRecording: boolean;
  isLoading: boolean;
  sessionSeconds: number;
  isAllowed: boolean | null;
  usedCredits: number | null;
  subscribedCredits: number | null;
}

const SILENCE_MS = 30000;
const MIN_SYNC_SECONDS = 10;

export function useNotesDictation(opts: UseNotesDictationOptions) {
  const {
    userRole,
    userId,
    caseDetailId,
    useAugnito,
    onTranscript,
    onPartial,
  } = opts;

  const [state, setState] = useState<DictationState>({
    isRecording: false,
    isLoading: false,
    sessionSeconds: 0,
    isAllowed: null,
    usedCredits: null,
    subscribedCredits: null,
  });

  const socketRef = useRef<WebSocket | null>(null);
  const mediaRecorderRef = useRef<any>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const silenceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sessionSecondsRef = useRef(0);
  const lastSyncedRef = useRef(0);
  const isSyncingRef = useRef(false);
  const isRecordingRef = useRef(false);

  const onTranscriptRef = useRef(onTranscript);
  onTranscriptRef.current = onTranscript;
  const onPartialRef = useRef(onPartial);
  onPartialRef.current = onPartial;

  const fetchQuota = useCallback(() => {
    if (!userRole || !userId) return;
    ReportService.getVoiceToTextInfo(
      userRole,
      userId,
      (response: any) => {
        setState((s) => ({
          ...s,
          isAllowed: response?.data?.allowed ?? null,
          usedCredits: response?.data?.used_credits ?? null,
          subscribedCredits: response?.data?.subscribed_credits ?? null,
        }));
      },
      () => {},
      () => {}
    );
  }, [userRole, userId]);

  const syncVoiceUsage = useCallback(
    (latestSeconds?: number) => {
      if (isSyncingRef.current) return;
      const current = latestSeconds ?? sessionSecondsRef.current;
      const secondsToStore = current - lastSyncedRef.current;
      if (secondsToStore < MIN_SYNC_SECONDS) return;
      isSyncingRef.current = true;
      ReportService.storeVoiceToTextSessionData(
        userRole,
        userId,
        secondsToStore,
        caseDetailId,
        () => {
          lastSyncedRef.current = current;
          isSyncingRef.current = false;
        },
        () => {
          isSyncingRef.current = false;
        },
        () => {
          isSyncingRef.current = false;
        }
      );
    },
    [userRole, userId, caseDetailId]
  );

  const cleanup = useCallback(() => {
    try {
      mediaRecorderRef.current?.stop?.();
    } catch {
      /* noop */
    }
    try {
      processorRef.current?.disconnect?.();
    } catch {
      /* noop */
    }
    try {
      sourceRef.current?.disconnect?.();
    } catch {
      /* noop */
    }
    try {
      audioContextRef.current?.close?.();
    } catch {
      /* noop */
    }
    mediaStreamRef.current?.getTracks?.().forEach((t) => t.stop());
    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    mediaRecorderRef.current = null;
    mediaStreamRef.current = null;
    audioContextRef.current = null;
    processorRef.current = null;
    sourceRef.current = null;
  }, []);

  const stop = useCallback(() => {
    const sock = socketRef.current;
    try {
      if (sock && sock.readyState === WebSocket.OPEN) sock.close();
    } catch {
      /* noop */
    }
    socketRef.current = null;
    isRecordingRef.current = false;
    cleanup();
    setState((s) => ({
      ...s,
      isRecording: false,
      isLoading: false,
    }));
    syncVoiceUsage();
    sessionSecondsRef.current = 0;
    lastSyncedRef.current = 0;
    setState((s) => ({ ...s, sessionSeconds: 0 }));
  }, [cleanup, syncVoiceUsage]);

  const resetSilence = useCallback(() => {
    if (silenceTimeoutRef.current) clearTimeout(silenceTimeoutRef.current);
    silenceTimeoutRef.current = setTimeout(() => {
      stop();
    }, SILENCE_MS);
  }, [stop]);

  const startDeepgram = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    mediaStreamRef.current = stream;

    if (typeof MediaRecorder === "undefined") {
      Notification({
        message: "Browser does not support audio recording",
        type: NotificationTypes.ERROR,
      });
      stop();
      return;
    }
    const recorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
    mediaRecorderRef.current = recorder;

    const url =
      (process.env.REACT_APP_VOICE_BASE_URL as string) +
      ApiRoutes.VOICE_TO_TEXT_ENABLE;
    const socket = new WebSocket(url);
    socketRef.current = socket;

    socket.onopen = () => {
      isRecordingRef.current = true;
      setState((s) => ({ ...s, isRecording: true, isLoading: false }));
      Notification({
        message: "Speech detection activated!",
        type: NotificationTypes.SUCCESS,
      });
      recorder.addEventListener("dataavailable", (event: any) => {
        if (event.data.size > 0 && socket.readyState === 1) {
          socket.send(event.data);
        }
      });
      recorder.start(1000);
      resetSilence();
    };
    socket.onmessage = (message: MessageEvent) => {
      const transcript = message.data as string;
      if (transcript) {
        onTranscriptRef.current?.(" " + transcript);
      }
      resetSilence();
    };
    socket.onclose = () => {
      Notification({
        message: "Speech detection ended!",
        type: NotificationTypes.SUCCESS,
      });
      isRecordingRef.current = false;
      cleanup();
      setState((s) => ({ ...s, isRecording: false, isLoading: false }));
    };
    socket.onerror = () => {
      Notification({
        message: "Voice connection failed",
        type: NotificationTypes.ERROR,
      });
      stop();
    };
  }, [cleanup, resetSilence, stop]);

  const startAugnito = useCallback(async () => {
    const url =
      (process.env.REACT_APP_VOICE_BASE_URL as string) +
      ApiRoutes.AUGNITO_VOICE_TO_TEXT_ENABLE;
    const socket = new WebSocket(url);
    socket.binaryType = "arraybuffer";
    socketRef.current = socket;

    socket.onopen = async () => {
      isRecordingRef.current = true;
      setState((s) => ({ ...s, isRecording: true, isLoading: false }));
      Notification({
        message: "Speech detection activated!",
        type: NotificationTypes.SUCCESS,
      });

      const ctx = new AudioContext({ sampleRate: 16000 });
      audioContextRef.current = ctx;
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      const source = ctx.createMediaStreamSource(stream);
      sourceRef.current = source;
      const processor = ctx.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;
      processor.onaudioprocess = (event: AudioProcessingEvent) => {
        const input = event.inputBuffer.getChannelData(0);
        const pcm16 = new Int16Array(input.length);
        for (let i = 0; i < input.length; i++) {
          pcm16[i] = Math.max(-1, Math.min(1, input[i])) * 0x7fff;
        }
        if (socket.readyState === WebSocket.OPEN) {
          socket.send(pcm16.buffer);
        }
      };
      source.connect(processor);
      processor.connect(ctx.destination);
      resetSilence();
    };
    socket.onmessage = (event: MessageEvent) => {
      let data: any;
      try {
        data = JSON.parse(event.data as string);
      } catch {
        return;
      }
      if (!data?.text) return;
      if (data.type === "partial") {
        onPartialRef.current?.(data.text);
      } else if (data.type === "final") {
        onTranscriptRef.current?.(" " + data.text);
        onPartialRef.current?.("");
      }
      resetSilence();
    };
    socket.onclose = () => {
      Notification({
        message: "Speech detection ended!",
        type: NotificationTypes.SUCCESS,
      });
      isRecordingRef.current = false;
      cleanup();
      setState((s) => ({ ...s, isRecording: false, isLoading: false }));
    };
    socket.onerror = () => {
      Notification({
        message: "Voice connection failed",
        type: NotificationTypes.ERROR,
      });
      stop();
    };
  }, [cleanup, resetSilence, stop]);

  const start = useCallback(async () => {
    if (isRecordingRef.current) return;
    if (state.isAllowed === false) {
      Notification({
        message:
          "Voice to text feature time limit exceeded. Contact Support Team",
        type: NotificationTypes.ERROR,
      });
      return;
    }
    setState((s) => ({ ...s, isLoading: true }));
    try {
      if (useAugnito) {
        await startAugnito();
      } else {
        await startDeepgram();
      }
    } catch (err) {
      Notification({
        message: "Microphone access denied",
        type: NotificationTypes.ERROR,
      });
      setState((s) => ({ ...s, isLoading: false }));
    }
  }, [state.isAllowed, useAugnito, startAugnito, startDeepgram]);

  useEffect(() => {
    if (!state.isRecording) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }
    if (intervalRef.current) return;
    intervalRef.current = setInterval(() => {
      sessionSecondsRef.current += 1;
      setState((s) => ({ ...s, sessionSeconds: sessionSecondsRef.current }));
      if (sessionSecondsRef.current % 10 === 0) {
        syncVoiceUsage();
      }
    }, 1000);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [state.isRecording, syncVoiceUsage]);

  useEffect(() => {
    return () => {
      if (socketRef.current) {
        try {
          socketRef.current.close();
        } catch {
          /* noop */
        }
      }
      cleanup();
    };
  }, [cleanup]);

  return {
    ...state,
    start,
    stop,
    fetchQuota,
  };
}
